import { API_CALL, SET_CUSTOMER_VIEW_TOGGLE, SET_AGENT_EXTRA_EARNINGS } from 'actions/actionConstants'
import requestGetAccountAgent from 'agentHub/api/requestGetAccountAgent'
import {
  FETCH_ACCOUNT_AGENT_HUB,
  FETCH_AGENT_STATUS,
  ONBOARD_AGENCY,
  FETCH_AGENT_AFFILIATION_OPTIONS,
  FETCH_OFFER_COMMISSION,
  CLEAR_OFFER_COMMISSION_DATA,
  SET_AGENCY_DETAILS,
  SET_AGENT_PAYOUT_DETAILS,
  FETCH_AGENT_HUB_BOOKING,
  PATCH_AGENT_HUB_CUSTOMER_ID,
} from './apiActionConstants'
import requestGetAgentAccountById from 'agentHub/api/requestGetAgentAccountById'
import requestGetAgentStatus from 'agentHub/api/requestGetAgentStatus'
import type { SetAgencyDetailsParams } from 'agentHub/api/requestPostSetAgencyDetails'
import requestPostSetAgencyDetails from 'agentHub/api/requestPostSetAgencyDetails'
import requestPostMarkAgentAsOnboarded from 'agentHub/api/requestPostMarkAgentAsOnboarded'
import type { AgentHubGetOffersCommissionBody } from 'agentHub/api/agentHubCommission'
import { getAccumulatedOfferCommissionById } from 'agentHub/api/agentHubCommission'
import { getAgentAffiliationOptions } from 'agentHub/api/agentHubAffiliationOptions'
import type { AppAction } from './types/ActionTypes'
import requestGetAgentStatusByToken from 'agentHub/api/requestGetAgentStatusByToken'
import type { AgentHubSetPayoutDetailsBody } from 'agentHub/api/requestSetAgentHubPayoutDetails'
import { requestSetAgentHubPayoutDetails } from 'agentHub/api/requestSetAgentHubPayoutDetails'
import { getAgentHubBookingByOrderId } from 'agentHub/api/agentHubGetBookingByOrderId'
import { requestPatchCustomerId } from 'agentHub/api/requestPatchCustomerId'
import { getBedbankRateKey } from './BedbankOfferActions'

/**
 * Fetches the agent hub account for the current user
 *
 * Note: Will only work for onboarded users
 */
export function fetchAccountAgentHub(accessToken?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.agentHub.agentHubAccount.account?.status === 'onboarded') {
      // already have the onboarded version, no need to refetch
      // (we might have the pending version, so if that's the case, refetch it)
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ACCOUNT_AGENT_HUB,
      request: () => requestGetAccountAgent(accessToken),
    })
  }
}

/**
 * Fetches the agent hub account for the agent given.
 *
 * Note: Will work for accounts that are still pending onboarding
 * @param agentId The ID of the agent to fetch the account for
 */
export function fetchAgentAccountByAgentId(agentId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.agentHub.agentHubAccount.account?.agentId === agentId) {
      // already have the current agents data, no need to refetch
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ACCOUNT_AGENT_HUB,
      request: () => requestGetAgentAccountById(agentId),
    })
  }
}

export function fetchAgentAffiliationOptions(region: App.AgentHubEnabledRegions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.agentHub?.affiliationOptions?.options[region]?.length > 0) {
      // already have the affiliation options, no need to refetch
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_AGENT_AFFILIATION_OPTIONS,
      key: region,
      request: () => getAgentAffiliationOptions(region),
    })
  }
}

export function checkAgentHubStatus(agentEmail: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.agentHub.agentStatus[agentEmail] !== undefined) {
      // already checked it
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_AGENT_STATUS,
      key: agentEmail,
      request: () => requestGetAgentStatus(agentEmail),
    })
  }
}

export function checkAgentHubStatusByAgentToken(token: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.agentHub.agentStatus[token] !== undefined) {
      // already checked it
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_AGENT_STATUS,
      key: token,
      request: () => requestGetAgentStatusByToken(token),
    })
  }
}

export function toggleCustomerView(customerView?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: SET_CUSTOMER_VIEW_TOGGLE,
      value: customerView ?? !state.agentHub.customerView,
    })
  }
}

export function setAgentPayoutDetailsAndMarkAsOnboarded(payoutDetails: AgentHubSetPayoutDetailsBody, agentId: string): AppAction {
  return {
    type: API_CALL,
    api: SET_AGENT_PAYOUT_DETAILS,
    request: async() => {
      await requestSetAgentHubPayoutDetails(payoutDetails)
      const updatedAccount = await requestPostMarkAgentAsOnboarded(agentId)
      return updatedAccount
    },
  }
}

export function setAgencyDetails(agencyDetails: SetAgencyDetailsParams): AppAction {
  return {
    type: API_CALL,
    api: SET_AGENCY_DETAILS,
    request: () => requestPostSetAgencyDetails(agencyDetails.agentId, agencyDetails),
  }
}

export function onboardAgency(agentId: string): AppAction {
  return {
    type: API_CALL,
    api: ONBOARD_AGENCY,
    request: async() => {
      const updatedAccount = await requestPostMarkAgentAsOnboarded(agentId)
      return updatedAccount
    },
  }
}

function addDateRangeToOfferCommission(offerCommission: AgentHubGetOffersCommissionBody, startDate?: string, endDate?: string): AgentHubGetOffersCommissionBody {
  if (!startDate || !endDate) return offerCommission

  if (offerCommission.type === 'flight') {
    return {
      ...offerCommission,
      startDate,
      endDate,
    }
  }

  if (offerCommission.dateRanges?.length) return offerCommission

  return {
    ...offerCommission,
    dateRanges: [{
      id: offerCommission.offerId,
      startDate,
      endDate,
    }],
  }
}

export function fetchAccumulatedOfferCommissionById(offerId: string, offerCommission: AgentHubGetOffersCommissionBody, opts?: {
  startDate?: string
  endDate?: string
  occupants?: Array<App.Occupants>
}): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const region = state.geo.currentRegionCode
    const cachedOffer = state.agentHub.offersCommissionMap[offerId]

    const hasPackages = offerCommission.packages.length > 0
    const cachedOfferHasPackages = (cachedOffer?.packages?.length || 0) > 0
    const isBedbankOffer = offerCommission.type === 'bedbank_hotel'
    const dateKey = opts?.startDate && opts.endDate ? `${opts.startDate}-${opts.endDate}` : undefined
    const isLoading = state.agentHub.offersCommissionLoading[offerId]
    const cachedDateKey = state.agentHub.offersCommissionDateKeys[offerId]
    const isNotBedbankAndExistsInCache = !isBedbankOffer && cachedOffer && cachedDateKey === dateKey
    const supersedesPackagelessBedbank = isBedbankOffer && hasPackages && !cachedOfferHasPackages

    if (((isLoading && cachedDateKey === dateKey) && !supersedesPackagelessBedbank) || isNotBedbankAndExistsInCache) {
      return
    }

    const offerCommissionWithDates = addDateRangeToOfferCommission(offerCommission, opts?.startDate, opts?.endDate)

    if (isBedbankOffer && opts?.startDate && opts?.endDate && opts?.occupants) {
      const selectedRate = state.offer.bedbankOfferRates[offerId]?.[getBedbankRateKey(opts?.occupants ?? [], opts?.startDate ?? '', opts?.endDate ?? '')]
      if (selectedRate && cachedOffer && cachedOfferHasPackages && hasPackages) {
        const selectedRateIds = new Set(selectedRate.map(rate => rate.id))
        const offerCommissionIds = new Set(cachedOffer.packages.map(pkg => pkg.packageId))
        if (selectedRateIds.size === offerCommissionIds.size && !(Array.from(selectedRateIds).some(id => !offerCommissionIds.has(id)))) {
          return
        }
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_COMMISSION,
      request: () => getAccumulatedOfferCommissionById(offerId, offerCommissionWithDates, region),
      key: offerId,
      dateKey,
    })
  }
}

export const clearOfferCommissionData = (offerId: string): AppAction => {
  return {
    type: CLEAR_OFFER_COMMISSION_DATA,
    key: offerId,
  }
}

export function fetchAgentHubBooking(orderId: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_AGENT_HUB_BOOKING,
    key: orderId,
    request: () => getAgentHubBookingByOrderId(orderId),
  }
}

export function setAgentExtraEarnings(amount: number): AppAction {
  return {
    type: SET_AGENT_EXTRA_EARNINGS,
    amount,
  }
}

export function registerCustomerId(agentId: string, customerId: string): AppAction {
  return {
    type: API_CALL,
    api: PATCH_AGENT_HUB_CUSTOMER_ID,
    key: customerId,
    request: async() => {
      const data = await requestPatchCustomerId(agentId, customerId)
      return { customerId: data.customerId! }
    },
  }
}
